import { Controller } from '../utils/controller';
import {
  formUtilHideError,
  formUtilIsErrorDisplayed,
  formUtilTogglePasswordVisibility,
  formUtilValidateEmail
} from '../utils/accountFormUtils';

interface IFormTarget {
  inputContainer: HTMLElement;
  input: HTMLInputElement;
  error: HTMLInputElement;
}

interface ISignUpActiveInputTargets {
  email: IFormTarget;
  password: IFormTarget;
}

export default class extends Controller {
  readonly cssClass = {
    checkboxSelected: 'elc-checkbox-selected',
    checkboxNotSelected: 'elc-checkbox-not-selected',
    errorField: 'elc-floating-label-input-errorField',
    errorInputContainer: 'elc-floating-label-input-error'
  };

  static targets = [
    'signUpEmailContainer',
    'signUpEmailInput',
    'signUpEmailError',
    'signUpPasswordContainer',
    'signUpPasswordInput',
    'signUpPasswordError',
    'passwordCriteria',
    'passwordCriteriaList'
  ];

  declare signUpEmailContainerTarget: HTMLElement;
  declare signUpEmailInputTarget: HTMLInputElement;
  declare signUpEmailErrorTarget: HTMLInputElement;
  declare signUpPasswordContainerTarget: HTMLElement;
  declare signUpPasswordInputTarget: HTMLInputElement;
  declare signUpPasswordErrorTarget: HTMLInputElement;
  declare passwordCriteriaTarget: HTMLElement;
  declare passwordCriteriaListTarget: HTMLElement;

  getSignUpActiveInputTargets(): ISignUpActiveInputTargets {
    return {
      email: {
        input: this.signUpEmailInputTarget,
        error: this.signUpEmailErrorTarget,
        inputContainer: this.signUpEmailContainerTarget
      },
      password: {
        input: this.signUpPasswordInputTarget,
        error: this.signUpPasswordErrorTarget,
        inputContainer: this.signUpPasswordContainerTarget
      }
    };
  }

  validateEmail(event: Event): void {
    const { email } = this.getSignUpActiveInputTargets();

    formUtilValidateEmail(this, event, email);
  }

  togglePasswordVisibility(event) {
    formUtilTogglePasswordVisibility(event);
  }

  checkPasswordCriteria(event): boolean {
    const passwordInput = event.target;

    passwordInput.value
      ? (this.passwordCriteriaTarget.style.display = 'block')
      : (this.passwordCriteriaTarget.style.display = 'none');

    let isPasswordValid = true;
    const rules = {
      minLength: '^.{8,}$',
      oneUppercase: '^.*(?=.*[A-Z]).*$',
      oneLowercase: '^.*(?=.*[a-z]).*$',
      oneDigit: '^.*[0-9].*$'
    };

    Array.from(this.passwordCriteriaListTarget.children).forEach(
      ({ dataset: { name }, classList }: HTMLElement) => {
        const rule = new RegExp(rules[name as string]);
        const isMatched = rule.test(passwordInput.value);

        isMatched
          ? (classList.remove('unmatched'), classList.add('matched'))
          : (classList.remove('matched'), classList.add('unmatched'), (isPasswordValid = false));
      }
    );

    return isPasswordValid;
  }

  validatePassword(event: Event): void {
    const { password } = this.getSignUpActiveInputTargets();
    const showPasswordCriteria = password.input
      .getAttributeNames()
      .includes('data-passwordstrength-target');

    if (event.type === 'blur' || this.isErrorDisplayed(password.error)) {
      password.input.value ? this.hideError(password) : this.showError(password);
    }

    if (showPasswordCriteria) {
      const isPasswordValid = this.checkPasswordCriteria(event);

      isPasswordValid ? this.hideError(password) : this.showError(password);
    }
  }

  isErrorDisplayed(target: HTMLElement): boolean {
    return formUtilIsErrorDisplayed(target);
  }

  showError(target: IFormTarget): void {
    const { error, input, inputContainer } = target;

    error.style.display = 'block';
    input.classList.add(this.cssClass.errorField);
    inputContainer.classList.add(this.cssClass.errorInputContainer);

    const {
      elc_account_errors: { password_strength_failed_reset_password, password_required },
      elc_errors: { email_address_required, email_address_invalid }
    } = window.site.translations;

    if (input.type === 'password' || input.id === 'passwordSignUp') {
      error.textContent = input.value ? password_strength_failed_reset_password : password_required;
    } else {
      error.textContent = input.value ? email_address_invalid : email_address_required;
    }
  }

  hideError(target: IFormTarget): void {
    const { error, input, inputContainer } = target;
    formUtilHideError(this, error, input, inputContainer);
  }

  submitForm(event: Event): void {
    const { email, password } = this.getSignUpActiveInputTargets();

    const hasErrors = this.isErrorDisplayed(email.error) || this.isErrorDisplayed(password.error);
    const hasNoEmail = !email.input.value;
    const hasNoPassword = !password.input.value;

    if (hasNoEmail || hasNoPassword || hasErrors) {
      event.preventDefault();

      hasNoEmail && this.showError(email);
      hasNoPassword && this.showError(password);
    } else {
      const submitButton = event.target as HTMLButtonElement;
      submitButton.disabled = true;
      submitButton.textContent = submitButton.getAttribute('data-disable-text');

      const form = submitButton.parentElement as HTMLFormElement;
      form.submit();
    }
  }
}
